import { useCallback, useEffect, useState } from 'react'
import { useParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { RiCheckboxCircleLine, RiTaskLine } from '@remixicon/react'
import type { Task, TaskStatus } from 'shared'
import { Alert, Badge, Card, EmptyState, PageHeader, SectionSpinner } from '../../components/ui'
import { api } from '../../utils/api'
import { toErrorMessage } from '../../lib/errors'
import { formatDate } from '../../lib/dates'
import { cx, focusRing } from '../../lib/utils'

export default function MemberTasks() {
  const { t, i18n } = useTranslation()
  const { workspaceId } = useParams<{ workspaceId: string }>()

  const [tasks, setTasks] = useState<Task[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const load = useCallback(async () => {
    if (!workspaceId) return
    const response = await api.get<{ data: Task[] }>(`/api/workspaces/${workspaceId}/tasks`)
    setTasks(response.data)
  }, [workspaceId])

  useEffect(() => {
    load()
      .catch((err) => setError(toErrorMessage(err)))
      .finally(() => setLoading(false))
  }, [load])

  const move = async (task: Task, status: TaskStatus) => {
    const previous = tasks
    setTasks((current) =>
      current.map((entry) => (entry.id === task.id ? { ...entry, status } : entry)),
    )
    setError(null)

    try {
      await api.put(`/api/workspaces/${workspaceId}/tasks/${task.id}`, { status })
      await load()
    } catch (err) {
      setTasks(previous)
      setError(toErrorMessage(err) ?? t('errors.generic'))
    }
  }

  if (loading) return <SectionSpinner />

  const open = tasks.filter((task) => task.status !== 'completed' && task.status !== 'cancelled')
  const done = tasks.filter((task) => task.status === 'completed')

  return (
    <>
      <PageHeader
        backTo={{ href: `/w/${workspaceId}`, label: t('timeline.title') }}
        title={t('tasks.title')}
        subtitle={t('tasks.subtitle')}
      />

      {error && <Alert className="mb-6">{error}</Alert>}

      {tasks.length === 0 ? (
        <Card>
          <EmptyState
            icon={<RiTaskLine className="size-6" aria-hidden="true" />}
            title={t('tasks.emptyTitle')}
            description={t('tasks.emptyDescription')}
          />
        </Card>
      ) : (
        <div className="space-y-8">
          <TaskList tasks={open} onMove={move} locale={i18n.language} />

          {done.length > 0 && (
            <section>
              <h2 className="mb-3 text-sm font-semibold text-gray-500 dark:text-gray-400">
                {t('tasks.completedHeading', { count: done.length })}
              </h2>
              <TaskList tasks={done} onMove={move} locale={i18n.language} />
            </section>
          )}
        </div>
      )}
    </>
  )
}

interface TaskListProps {
  tasks: Task[]
  onMove: (task: Task, status: TaskStatus) => void
  locale: string
}

function TaskList({ tasks, onMove, locale }: TaskListProps) {
  const { t } = useTranslation()

  if (tasks.length === 0) return null

  return (
    <ul className="space-y-3">
      {tasks.map((task) => {
        const completed = task.status === 'completed'
        const overdue = !completed && task.due_at && new Date(task.due_at) < new Date()

        return (
          <li key={task.id}>
            <Card className="flex items-start gap-4 p-4">
              <button
                type="button"
                role="checkbox"
                aria-checked={completed}
                aria-label={t(completed ? 'tasks.reopen' : 'tasks.complete')}
                onClick={() => onMove(task, completed ? 'in_progress' : 'completed')}
                className={cx(
                  'mt-0.5 flex size-5 shrink-0 items-center justify-center rounded-full border transition',
                  completed
                    ? 'border-emerald-500 bg-emerald-500 text-white'
                    : 'border-gray-300 hover:border-emerald-400 dark:border-gray-700',
                  focusRing,
                )}
              >
                {completed && <RiCheckboxCircleLine className="size-4" aria-hidden="true" />}
              </button>

              <div className="min-w-0 flex-1">
                <p
                  className={cx(
                    'text-sm font-medium',
                    completed
                      ? 'text-gray-400 line-through dark:text-gray-500'
                      : 'text-gray-900 dark:text-gray-50',
                  )}
                >
                  {task.title}
                </p>
                {task.description && (
                  <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
                    {task.description}
                  </p>
                )}
                <div className="mt-2 flex flex-wrap items-center gap-2 text-xs text-gray-500 dark:text-gray-400">
                  {task.due_at && (
                    <span className={overdue ? 'font-medium text-red-600 dark:text-red-400' : ''}>
                      {t('tasks.dueOn', { date: formatDate(task.due_at, locale) })}
                    </span>
                  )}
                  {task.assigned_to_name && <span>{task.assigned_to_name}</span>}
                </div>
              </div>

              {task.priority === 'high' && !completed && (
                <Badge variant="warning">{t('tasks.priority_high')}</Badge>
              )}
            </Card>
          </li>
        )
      })}
    </ul>
  )
}
